import { organizedTourBookingPath } from "../constants/routes";
import { tokenToPublicOfferUrl } from "./organizedTourPublicQuoteUrl";
import { resolvePublicBaseUrl } from "../../../utils/resolvePublicBaseUrl";
import { buildPublicFlightOrDynamicOfferUrl } from "../../../utils/publicOfferPaths";

const BOOKING_REF_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function parseNum(raw) {
  if (raw == null || raw === "") return null;
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  const s = String(raw)
    .replace(/[\s\u200f\u200e]/g, "")
    .replace(/,/g, "")
    .replace(/₪/g, "")
    .trim();
  if (s === "" || s === "-") return null;
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : null;
}

function parseMaybeJson(value) {
  if (value == null) return null;
  if (typeof value === "object") return value;
  if (typeof value === "string") {
    const t = value.trim();
    if ((t.startsWith("{") && t.endsWith("}")) || (t.startsWith("[") && t.endsWith("]"))) {
      try {
        return JSON.parse(t);
      } catch {
        return null;
      }
    }
  }
  return null;
}

function extendedFirst(extRaw) {
  const e = parseMaybeJson(extRaw) ?? extRaw;
  if (Array.isArray(e)) {
    const z = e[0];
    return z && typeof z === "object" ? z : null;
  }
  if (e && typeof e === "object") return e;
  return null;
}

function orderResponseObj(row) {
  const or = row?.order_response ?? row?.orderResponse;
  const o = parseMaybeJson(or) ?? or;
  if (o && typeof o === "object") return o;
  return null;
}

function normalizeDestValue(v) {
  if (v == null) return null;
  if (Array.isArray(v)) {
    const parts = v
      .map((x) =>
        x && typeof x === "object" ? x.name ?? x.label ?? x.city ?? x.destination : x
      )
      .filter((x) => x != null && String(x).trim() !== "");
    return parts.length ? parts.join(", ") : null;
  }
  if (typeof v === "object") {
    const s = v.name ?? v.label ?? v.city ?? v.destination;
    return s != null && String(s).trim() !== "" ? String(s).trim() : null;
  }
  const s = String(v).trim();
  return s || null;
}

function shortRouteString(v) {
  if (v == null) return null;
  if (typeof v === "string") {
    const t = v.trim();
    return t || null;
  }
  if (typeof v === "object") {
    const t = v.label ?? v.name ?? v.title ?? v.routeName;
    return t != null ? String(t).trim() || null : null;
  }
  return null;
}

function formatIlsCell(n) {
  if (n == null || !Number.isFinite(n) || n === 0) return null;
  return `₪${n.toLocaleString("he-IL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatHrDisplay(rawBookingRef) {
  if (rawBookingRef == null || String(rawBookingRef).trim() === "") return null;
  const s = String(rawBookingRef).trim();
  if (BOOKING_REF_UUID_RE.test(s)) return null;
  if (/^HR-/i.test(s)) return s;
  if (/^\d+$/.test(s)) return `HR-${s}`;
  return s;
}

function resolveDestination(row, ex, orObj) {
  const steps = [
    ["row.destination", normalizeDestValue(row?.destination ?? row?.destDestination)],
    ["row.route", shortRouteString(row?.route)],
    ["row.destinations", normalizeDestValue(row?.destinations)],
    ["extended.destination", normalizeDestValue(ex?.destination)],
    ["extended.route", shortRouteString(ex?.route)],
    ["order_response.destination", normalizeDestValue(orObj?.destination)],
    ["order_response.route", shortRouteString(orObj?.route)],
  ];
  for (const [field, val] of steps) {
    if (val != null && String(val).trim() !== "") {
      return { value: String(val).trim(), field };
    }
  }
  return { value: null, field: null };
}

function resolveRouteDisplay(row, ex, tourn, orObj) {
  const tourName =
    shortRouteString(ex?.tourName) ??
    shortRouteString(ex?.tour_name) ??
    shortRouteString(tourn?.tourName) ??
    shortRouteString(tourn?.name);
  if (tourName) return { value: tourName, field: "tourName" };
  const route =
    shortRouteString(ex?.route) ??
    shortRouteString(tourn?.route) ??
    shortRouteString(orObj?.route);
  if (route) return { value: route, field: "route" };
  const apiHotel = row?.hotel_name ?? row?.hotelName;
  if (apiHotel && String(apiHotel).trim() && String(apiHotel).trim() !== "טיול מאורגן") {
    return { value: String(apiHotel).trim(), field: "api.hotel_name" };
  }
  return { value: "טיול מאורגן", field: "fallback" };
}

function resolvePriceIncludingHandling(row, ex, orObj, op, snap, handling) {
  const fee = handling != null && Number.isFinite(handling) ? handling : 0;
  const tryNum = (v) => parseNum(v);

  const steps = [
    {
      key: "display_total_ils",
      addFee: false,
      fn: () => tryNum(op?.display_total_ils ?? snap?.display_total_ils),
    },
    {
      key: "canonical_total_price_ils",
      addFee: true,
      fn: () => tryNum(op?.canonical_total_price_ils ?? snap?.canonical_total_price_ils),
    },
    { key: "final_customer_price", addFee: false, fn: () => tryNum(op?.final_customer_price) },
    { key: "admin_total_amount", addFee: false, fn: () => tryNum(op?.admin_total_amount) },
    {
      key: "total_price_ils",
      addFee: false,
      fn: () => tryNum(row?.total_price_ils ?? row?.totalPriceIls ?? row?.final_price_ils ?? row?.finalPriceIls),
    },
    { key: "extended.display_total_ils", addFee: false, fn: () => tryNum(ex?.display_total_ils) },
    { key: "order_response.display_total_ils", addFee: false, fn: () => tryNum(orObj?.display_total_ils) },
    {
      key: "pricingSnapshot.priceWithCommission",
      addFee: true,
      fn: () => tryNum(snap?.priceWithCommission),
    },
  ];

  for (const s of steps) {
    const base = s.fn();
    if (base != null && base > 0) {
      const total = s.addFee ? round2(base + fee) : base;
      return { value: total, field: s.key, addFee: s.addFee };
    }
  }

  const apiTotal = tryNum(row?.total_price ?? row?.totalPrice);
  if (apiTotal != null && apiTotal > 0) {
    return { value: apiTotal, field: "api.total_price", addFee: false };
  }

  return { value: null, field: null, addFee: false };
}

function round2(x) {
  return Math.round(x * 100) / 100;
}

function resolvePublicLink(row, origin) {
  const o = origin || resolvePublicBaseUrl({ skipTrace: true });
  const tok = row?.public_access_token ?? row?.publicAccessToken;
  if (tok) {
    const full = tokenToPublicOfferUrl(tok);
    if (full && !String(full).includes("/packages/p")) return full;
  }
  const dyn =
    row?.dynamic_package_id ?? row?.dynamicPackageId ?? row?.dynamicid ?? row?.dynamicId ?? null;
  if (dyn != null) {
    const u = buildPublicFlightOrDynamicOfferUrl(o, dyn, { flightOnly: false });
    if (u) return u;
  }
  const raw = row?.public_share_url ?? row?.publicShareUrl ?? null;
  if (raw && !String(raw).includes("/packages/p")) {
    const s = String(raw).trim();
    if (s.startsWith("http")) return s;
    return o ? `${String(o).replace(/\/$/, "")}${s.startsWith("/") ? s : `/${s}`}` : s;
  }
  const id = row?.id != null ? String(row.id).trim() : "";
  if (id) {
    const p = organizedTourBookingPath(id);
    return o ? `${String(o).replace(/\/$/, "")}${p.startsWith("/") ? p : `/${p}`}` : p;
  }
  return null;
}

function datesDisplayFromRow(row) {
  const dep = row?.dep_date ?? row?.depDate ?? "";
  const ret = row?.ret_date ?? row?.retDate ?? "";
  const ds = String(dep).trim();
  const rs = String(ret).trim();
  if (ds && rs) return `${ds} · ${rs}`;
  return ds || rs || null;
}

/**
 * SSOT למיפוי שורת טיוטת Organized Tour בטבלת drafts.
 *
 * @param {object} row — פריט גולמי מ־/api/lds-drafts
 * @param {{ origin?: string }} [ctx]
 * @returns {{
 *   destination: string|null,
 *   routeDisplay: string|null,
 *   finalPriceDisplay: number|null,
 *   handlingFeeDisplay: string|null,
 *   publicLink: string|null,
 *   hrDisplay: string|null,
 *   datesDisplay: string|null,
 *   handlingFeeIls: number|null,
 *   _trace: { priceField: string|null, destinationField: string|null, routeField: string|null, missing: string[] }
 * }}
 */
export function buildOrganizedTourDraftRow(row, ctx = {}) {
  const traceEnabled = import.meta.env.DEV;
  const missing = [];
  const ex = extendedFirst(row?.extended ?? row?.extendedDeal);
  const tourn = ex?.tour && typeof ex.tour === "object" ? ex.tour : null;
  const orObj = orderResponseObj(row);
  const opRaw = row?.ourprice ?? row?.ourPrice;
  let op = opRaw && typeof opRaw === "object" && !Array.isArray(opRaw) ? opRaw : null;
  if (!op && typeof opRaw === "string") {
    const parsed = parseMaybeJson(opRaw);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) op = parsed;
  }
  const snap = op?.pricingSnapshot && typeof op.pricingSnapshot === "object" ? op.pricingSnapshot : null;
  const handling = parseNum(row?.handlingfee ?? row?.handling_fee);

  const { value: destination, field: destinationField } = resolveDestination(row, ex, orObj);
  const { value: routeDisplay, field: routeField } = resolveRouteDisplay(row, ex, tourn, orObj);
  const priceRes = resolvePriceIncludingHandling(row, ex, orObj, op, snap, handling);
  const finalPriceDisplay = priceRes.value;
  const priceField = priceRes.field;

  const handlingFeeIls = handling;
  const handlingFeeDisplay = formatIlsCell(handlingFeeIls);

  const origin = ctx.origin;
  const publicLink = resolvePublicLink(row, origin);
  const hrDisplay = formatHrDisplay(row?.booking_ref ?? row?.bookingRef);
  const datesDisplay = datesDisplayFromRow(row);

  if (!destination) missing.push("destination");
  if (finalPriceDisplay == null) missing.push("finalPrice");
  if (!publicLink) missing.push("publicLink");

  if (traceEnabled) {
    // eslint-disable-next-line no-console
    console.log("[DRAFTS_ORG_ROW_BUILD]", {
      id: row?.id,
      destination,
      destinationField,
      routeDisplay,
      routeField,
      finalPriceDisplay,
      priceField,
      handlingFeeIls,
      publicLink: publicLink ? "(set)" : null,
      hrDisplay,
    });
    if (priceField) {
      // eslint-disable-next-line no-console
      console.log("[DRAFTS_ORG_PRICE_FOUND]", { field: priceField, value: finalPriceDisplay });
    }
    if (routeField) {
      // eslint-disable-next-line no-console
      console.log("[DRAFTS_ORG_ROUTE_FOUND]", { field: routeField, routeDisplay });
    }
    if (missing.length) {
      // eslint-disable-next-line no-console
      console.warn("[DRAFTS_ORG_MISSING_FIELDS]", { id: row?.id, missing });
    }
  }

  return {
    destination,
    routeDisplay,
    finalPriceDisplay,
    handlingFeeDisplay,
    publicLink,
    hrDisplay,
    datesDisplay,
    handlingFeeIls,
    _trace: {
      priceField,
      destinationField,
      routeField,
      missing,
    },
  };
}
